import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Search, Filter, ShoppingCart, Trash2, Eye, CreditCard, DollarSign, Users } from "lucide-react";
import { getApiErrorPayload, salesAPI } from "@/lib/api";
import { Sale, SaleStatus, PaymentStatus } from "@/types";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime, getBestBadgeVariant } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { HubFilters } from "@/features/sales/hubFilters";
import { confirmAction } from "@/components/ui/confirm-dialog";

const SalesPage: React.FC<{ filters?: HubFilters }> = ({ filters }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { toast } = useToast();
	const currency = useCurrency();

	const [sales, setSales] = useState<Sale[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Pagination and filters
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalSales, setTotalSales] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedStatus, setSelectedStatus] = useState<SaleStatus | "">("");
	const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<PaymentStatus | "">("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
	const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
	const [paymentViewMode, setPaymentViewMode] = useState<"view" | "create">("view");
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [paymentForm, setPaymentForm] = useState({
		amount: "",
		paymentMethod: "cash" as "cash" | "card" | "bank_transfer" | "check" | "credit",
		notes: "",
	});
	const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

	// When embedded in the sales hub, the hub toolbar owns the filters
	useEffect(() => {
		if (!filters) return;
		setSearchTerm(filters.search);
		setSelectedStatus(filters.status as SaleStatus | "");
		setSelectedPaymentStatus(filters.paymentStatus as PaymentStatus | "");
		setDateFrom(filters.dateFrom);
		setDateTo(filters.dateTo);
		setCurrentPage(1);
	}, [filters]);

	useEffect(() => {
		fetchSales();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, itemsPerPage, searchTerm, selectedStatus, selectedPaymentStatus, dateFrom, dateTo]);

	const fetchSales = async () => {
		try {
			setIsLoading(true);
			setError(null);

			const response = await salesAPI.getAll({
				page: currentPage,
				limit: itemsPerPage,
				search: searchTerm || undefined,
				status: selectedStatus || undefined,
				paymentStatus: selectedPaymentStatus || undefined,
				startDate: dateFrom || undefined,
				endDate: dateTo || undefined,
				groupSplits: true,
			});

			setSales(response.sales || []);
			setTotalSales(response.total || 0);
			setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
			// Initialize itemsPerPage from backend response if available
			if (response.limit && response.limit !== itemsPerPage) {
				setItemsPerPage(response.limit);
			}
		} catch (err) {
			const payload = getApiErrorPayload(err);
			setError(t(payload.key, (payload.params ?? {}) as Record<string, string>));
			console.error("Sales fetch error:", err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCreatePayment = async () => {
		if (!selectedSale) return;

		const amount = parseFloat(paymentForm.amount);
		if (isNaN(amount) || amount <= 0) {
			toast({
				title: t("common.error"),
				description: t("sales.errors.invalidPaymentAmount"),
				variant: "destructive",
			});
			return;
		}

		const remainingAmount =
			selectedSale.remainingAmount ?? selectedSale.totalAmount - (selectedSale.paidAmount ?? 0);
		if (amount > remainingAmount) {
			toast({
				title: t("common.error"),
				description: t("sales.errors.paymentExceedsRemaining"),
				variant: "destructive",
			});
			return;
		}

		try {
			setIsSubmittingPayment(true);
			await salesAPI.addPayment(selectedSale.id, {
				amount,
				paymentMethod: paymentForm.paymentMethod,
				notes: paymentForm.notes || undefined,
			});
			toast({
				title: t("common.success"),
				description: t("sales.messages.paymentCreated"),
			});
			setPaymentForm({ amount: "", paymentMethod: "cash", notes: "" });
			setIsPaymentDialogOpen(false);
			setSelectedSale(null);
			fetchSales();
		} catch (err) {
			toast({
				title: t("common.error"),
				description: t("sales.errors.paymentFailed"),
				variant: "destructive",
			});
		} finally {
			setIsSubmittingPayment(false);
		}
	};

	const handleDeleteSale = async (saleId: number | string) => {
		if (await confirmAction(t("sales.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await salesAPI.delete(saleId);
				toast({
					title: t("common.success"),
					description: t("sales.messages.deleted"),
				});
				fetchSales();
			} catch (err) {
				toast({
					title: t("common.error"),
					description: t("sales.messages.deleteFailed"),
					variant: "destructive",
				});
			}
		}
	};

	const getStatusBadge = (status: SaleStatus) => {
		const statusConfig = {
			[SaleStatus.PENDING]: {
				variant: "warning",
				text: t("sales.filters.pending"),
			},
			[SaleStatus.CONFIRMED]: {
				variant: "success",
				text: t("sales.filters.confirmed"),
			},
			[SaleStatus.CANCELLED]: {
				variant: "danger",
				text: t("sales.filters.cancelled"),
			},
			[SaleStatus.REFUNDED]: {
				variant: "violet",
				text: t("sales.filters.refunded"),
			},
		};

		const config = statusConfig[status];
		return <Badge variant={getBestBadgeVariant(config.variant)}>{config.text}</Badge>;
	};

	const getPaymentStatusBadge = (status: PaymentStatus) => {
		const statusConfig = {
			[PaymentStatus.PENDING]: {
				variant: "warning",
				text: t("sales.filters.pending"),
			},
			[PaymentStatus.PAID]: {
				variant: "success",
				text: t("sales.filters.paid"),
			},
			[PaymentStatus.PARTIAL]: {
				variant: "warning",
				text: t("sales.filters.partial"),
			},
			[PaymentStatus.OVERDUE]: {
				variant: "danger",
				text: t("sales.filters.overdue"),
			},
			[PaymentStatus.REFUNDED]: {
				variant: "violet",
				text: t("sales.filters.refunded"),
			},
		};

		const config = statusConfig[status];
		return <Badge variant={getBestBadgeVariant(config.variant)}>{config.text}</Badge>;
	};

	if (isLoading && currentPage === 1) {
		return <Loading />;
	}

	return (
		<div className="space-y-6">
			{/* Filters and Search (hidden when the hub toolbar owns them) */}
			{!filters && (
				<Card>
					<CardHeader className="flex flex-row items-center justify-between py-2">
						<CardTitle className="flex items-center gap-2 text-base">
							<Filter className="h-5 w-5" />
							{t("sales.filters.title")}
						</CardTitle>
						<Button
							variant="link"
							className="text-destructive"
							onClick={() => {
								setSearchTerm("");
								setSelectedStatus("");
								setSelectedPaymentStatus("");
								setDateFrom("");
								setDateTo("");
								setCurrentPage(1);
							}}
						>
							{t("sales.filters.clearFilters")}
						</Button>
					</CardHeader>
					<CardContent>
						<div className="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-5">
							<div className="relative">
								<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
								<Input
									placeholder={t("sales.filters.searchPlaceholder")}
									value={searchTerm}
									onChange={e => setSearchTerm(e.target.value)}
									className="ps-10"
								/>
							</div>

							<select
								value={selectedStatus}
								onChange={e => setSelectedStatus(e.target.value as SaleStatus | "")}
								className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<option value="">{t("sales.filters.allStatuses")}</option>
								<option value={SaleStatus.PENDING}>{t("sales.filters.pending")}</option>
								<option value={SaleStatus.CONFIRMED}>{t("sales.filters.confirmed")}</option>
								<option value={SaleStatus.CANCELLED}>{t("sales.filters.cancelled")}</option>
								<option value={SaleStatus.REFUNDED}>{t("sales.filters.refunded")}</option>
							</select>

							<select
								value={selectedPaymentStatus}
								onChange={e => setSelectedPaymentStatus(e.target.value as PaymentStatus | "")}
								className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<option value="">{t("sales.filters.allPaymentStatuses")}</option>
								<option value={PaymentStatus.PENDING}>{t("sales.filters.pending")}</option>
								<option value={PaymentStatus.PAID}>{t("sales.filters.paid")}</option>
								<option value={PaymentStatus.PARTIAL}>{t("sales.filters.partial")}</option>
								<option value={PaymentStatus.REFUNDED}>{t("sales.filters.refunded")}</option>
							</select>

							<Input
								type="date"
								value={dateFrom}
								onChange={e => setDateFrom(e.target.value)}
								placeholder={t("sales.filters.fromDate")}
							/>

							<Input
								type="date"
								value={dateTo}
								onChange={e => setDateTo(e.target.value)}
								placeholder={t("sales.filters.toDate")}
							/>
						</div>
					</CardContent>
				</Card>
			)}

			{sales.length > 0 && (
				<>
					{/* Sales Table */}
					<Card>
						<CardHeader className="flex flex-row items-center justify-between">
							<div className="space-y-2">
								<CardTitle className="flex items-center gap-2 text-base">
									<ShoppingCart className="w-6 h-6 shrink-0" />
									<span>{t("sales.header.title")}</span>
								</CardTitle>
								<CardDescription>{t("sales.header.description")}</CardDescription>
							</div>
							{!filters && (
								<PermissionGate permission="sales.create">
									<Button className="flex items-center gap-2" onClick={() => navigate("/sales/new")}>
										<Plus className="h-4 w-4" />
										{t("sales.header.addButton")}
									</Button>
								</PermissionGate>
							)}
						</CardHeader>
						<CardContent>
							<div className="record-surface">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("common.number")}</TableHead>
											<TableHead>{t("sales.table.headers.customer")}</TableHead>
											<TableHead className="text-center">
												{t("sales.table.headers.date")}
											</TableHead>
											<TableHead className="text-center">
												{t("sales.table.headers.items")}
											</TableHead>
											<TableHead className="text-center">
												{t("sales.table.headers.total")}
											</TableHead>
											<TableHead className="text-center">
												{t("sales.table.headers.status")}
											</TableHead>
											<TableHead className="text-center">
												{t("sales.table.headers.paymentStatus")}
											</TableHead>
											<TableHead className="text-center">
												{t("sales.table.headers.actions")}
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{sales.map(sale => {
											const splitCount = sale.splitChildren?.length ?? 0;
											const isSplit = sale.splitStatus === "split" && splitCount > 0;
											return (
												<TableRow key={sale.id}>
													<TableCell>
														<div className="flex flex-col items-start gap-1">
															<span>{sale.id}</span>
															{isSplit ? (
																<Badge
																	variant="outline"
																	className="gap-1 border-primary/25 bg-primary/5 text-[10px] text-primary"
																>
																	<Users className="h-3 w-3" />
																	{t("sales.split.count", { count: splitCount })}
																</Badge>
															) : null}
														</div>
													</TableCell>
													<TableCell>
														<div className="flex flex-col gap-1">
															{sale.customer ? (
																<span>
																	{sale.customer.firstName} {sale.customer.lastName}
																</span>
															) : (
																<span className="text-muted-foreground">
																	{t("sales.card.cashCustomer")}
																</span>
															)}
															{sale.orderSource ? (
																<Badge variant="outline" className="w-fit text-[10px]">
																	{t(`sales.source.${sale.orderSource}`, {
																		defaultValue: sale.orderSource,
																	})}
																</Badge>
															) : null}
														</div>
													</TableCell>
													<TableCell className="text-center text-muted-foreground">
														{formatDateTime(sale.createdAt)}
													</TableCell>
													<TableCell className="text-center">
														<div className="flex justify-center items-center gap-2">
															<ShoppingCart className="h-4 w-4 text-muted-foreground" />
															{sale.items?.length || 0}
														</div>
													</TableCell>
													<TableCell className="text-center font-medium">
														<Price value={sale.totalAmount} currency={currency} />
													</TableCell>
													<TableCell className="text-center">
														{getStatusBadge(sale.status)}
													</TableCell>
													<TableCell className="text-center">
														{getPaymentStatusBadge(sale.paymentStatus)}
													</TableCell>
													<TableCell className="text-center">
														<div className="flex items-center justify-center gap-2">
															<PermissionGate permission="sales.read">
																<Button
																	variant="outline"
																	size="icon"
																	onClick={() => navigate(`/sales/${sale.id}`)}
																	title={t("common.view")}
																	aria-label={t("common.view")}
																>
																	<Eye className="h-4 w-4 text-primary" />
																</Button>
															</PermissionGate>
															{!isSplit ? (
																<>
																	<PermissionGate permission="sales.read">
																		<Button
																			variant="outline"
																			size="icon"
																			onClick={() => {
																				setSelectedSale(sale);
																				setPaymentViewMode("view");
																				setIsPaymentDialogOpen(true);
																			}}
																			title={t("sales.actions.viewPayment")}
																			aria-label={t("sales.actions.viewPayment")}
																		>
																			<CreditCard className="h-4 w-4 text-success" />
																		</Button>
																	</PermissionGate>
																	<PermissionGate permission="sales.update">
																		<Button
																			variant="outline"
																			size="icon"
																			onClick={() => {
																				setSelectedSale(sale);
																				setPaymentViewMode("create");
																				setPaymentForm({
																					amount: "",
																					paymentMethod: "cash",
																					notes: "",
																				});
																				setIsPaymentDialogOpen(true);
																			}}
																			title={t("sales.actions.createPayment")}
																			aria-label={t(
																				"sales.actions.createPayment",
																			)}
																		>
																			<DollarSign className="h-4 w-4 text-primary" />
																		</Button>
																	</PermissionGate>
																	<PermissionGate permission="sales.delete">
																		<Button
																			variant="outline"
																			size="icon"
																			onClick={() => handleDeleteSale(sale.id)}
																			title={t("common.delete")}
																			aria-label={t("common.delete")}
																		>
																			<Trash2 className="h-4 w-4 text-destructive" />
																		</Button>
																	</PermissionGate>
																</>
															) : null}
														</div>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</div>
						</CardContent>
					</Card>

					{/* Pagination */}
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						totalItems={totalSales}
						itemsPerPage={itemsPerPage}
						onPageChange={setCurrentPage}
						onPageSizeChange={setItemsPerPage}
						itemName="sales.pagination.sales"
					/>
				</>
			)}
			{/* Error State */}
			{error && (
				<Card className="border-destructive">
					<CardContent className="pt-6">
						<div className="text-center">
							<ShoppingCart className="h-12 w-12 text-destructive mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">{t("sales.errors.loadingError")}</h3>
							<p className="text-muted-foreground mb-4">{error}</p>
							<Button onClick={fetchSales}>{t("common.tryAgain")}</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Empty State */}
			{!isLoading && sales.length === 0 && !error && (
				<Card>
					<CardContent className="pt-6">
						<div className="text-center">
							<ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">{t("sales.empty.title")}</h3>
							<p className="text-muted-foreground mb-4">{t("sales.empty.description")}</p>
							{!filters && (
								<PermissionGate permission="sales.create">
									<Button onClick={() => navigate("/sales/new")}>
										<Plus className="h-4 w-4 mx-2" />
										{t("sales.header.addButton")}
									</Button>
								</PermissionGate>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Payment Dialog */}
			<Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							{paymentViewMode === "view"
								? t("sales.actions.viewPayment")
								: t("sales.actions.createPayment")}
						</DialogTitle>
						<DialogDescription>
							{paymentViewMode === "view"
								? t("sales.dialogs.viewPaymentDescription")
								: t("sales.dialogs.createPaymentDescription")}
						</DialogDescription>
					</DialogHeader>
					{selectedSale && (
						<div className="space-y-4 py-4">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										{t("sales.table.headers.reference")}
									</label>
									<p className="text-lg font-semibold">#{selectedSale.reference}</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										{t("sales.table.headers.total")}
									</label>
									<p className="text-lg font-semibold">
										<Price value={selectedSale.totalAmount} currency={currency} />
									</p>
								</div>
							</div>
							{paymentViewMode === "view" ? (
								<div className="space-y-2">
									<label className="text-sm font-medium">
										{t("sales.table.headers.paymentStatus")}
									</label>
									<div>{getPaymentStatusBadge(selectedSale.paymentStatus)}</div>
									{selectedSale.paymentStatus === PaymentStatus.PARTIAL && (
										<div className="text-sm text-muted-foreground">
											{t("sales.payment.partialPaymentInfo")}
										</div>
									)}
								</div>
							) : (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="space-y-2">
										<label className="text-sm font-medium">{t("sales.payment.amount")}</label>
										<Input
											aria-label={t("sales.payment.amount")}
											type="number"
											placeholder={t("sales.payment.amountPlaceholder")}
											min="0"
											step="0.01"
											value={paymentForm.amount}
											onChange={e =>
												setPaymentForm(prev => ({ ...prev, amount: e.target.value }))
											}
										/>
									</div>
									<div className="space-y-2">
										<label className="text-sm font-medium">
											{t("sales.payment.paymentMethod")}
										</label>
										<select
											aria-label={t("sales.payment.paymentMethod")}
											className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
											value={paymentForm.paymentMethod}
											onChange={e =>
												setPaymentForm(prev => ({
													...prev,
													paymentMethod: e.target.value as typeof paymentForm.paymentMethod,
												}))
											}
										>
											<option value="cash">{t("sales.paymentMethods.cash")}</option>
											<option value="card">{t("sales.paymentMethods.card")}</option>
											<option value="bank_transfer">
												{t("sales.paymentMethods.bank_transfer")}
											</option>
											<option value="check">{t("sales.paymentMethods.cheque")}</option>
											<option value="credit">{t("sales.paymentMethods.credit")}</option>
										</select>
									</div>
								</div>
							)}
						</div>
					)}
					<DialogFooter>
						<Button variant="outline" className="px-12" onClick={() => setIsPaymentDialogOpen(false)}>
							{paymentViewMode === "view" ? t("common.close") : t("common.cancel")}
						</Button>
						{paymentViewMode === "create" && (
							<Button
								className="px-12"
								onClick={handleCreatePayment}
								disabled={
									isSubmittingPayment || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0
								}
							>
								{isSubmittingPayment ? t("common.loading") : t("common.create")}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default SalesPage;
